<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: SiteStatsGrowth                                       |
| Section: views/overviews                                         |
| Purpose: Site-stats tab showing growth trends over time.         |
+------------------------------------------------------------------+
-->
<template>
    <div :class="embedded ? null : 'growth-page'">
        <div :class="embedded ? null : 'wrapper overview-wrapper overview-gap-48'">
            <div v-if="!embedded" class="overview-header stats-header">
                <!-- <div class="stats-header-actions">
                <BtnSecondary class="BtnSecondary" label="Statistieken" url="/statistieken" />
            </div> -->
                <h1 class="overview-header-text">{{ $t('siteStatsGrowth.title') }}</h1>
                <p class="subtitle overview-header-text">{{ $t('siteStatsGrowth.subtitle') }}</p>
                <p v-if="latest" class="updated-at overview-header-text">{{ $t('siteStatsGrowth.lastUpdate') }}: {{
                    formatDate(latest.date) }}
                </p>
                <div v-if="latest" class="warning-banner">
                    <p>{{ $t('siteStatsGrowth.warningMissingHistory') }}</p>
                    <p v-if="trackingStartLabel">{{ $t('siteStatsGrowth.trackingStartedOn', {
                        date: trackingStartLabel
                    }) }}</p>
                </div>
            </div>

            <div v-if="latest" class="summary-stack">
                <div class="summary-toolbar">
                    <div class="summary-title">
                        <h2>{{ $t('siteStatsGrowth.summary') }}</h2>
                        <p>{{ $t('siteStatsGrowth.growthPerPeriod') }}: {{ deltaModeLabel }}</p>
                    </div>
                </div>

                <div class="stats-summary">
                    <div v-for="item in summaryItems" :key="item.label" class="summary-card">
                        <p class="summary-label">{{ item.label }}</p>
                        <p class="summary-value">
                            {{ formatNumber(item.value) }}
                            <span class="summary-delta summary-delta--since">
                                {{ formatDelta(item.deltaSinceStart) }} {{ $t('siteStatsGrowth.sinceStart') }}
                            </span>
                        </p>
                        <p v-if="item.deltaPeriod !== null" class="summary-period">
                            {{ formatDelta(item.deltaPeriod) }} {{ $t('siteStatsGrowth.lastPeriod') }} {{
                                deltaModeLabelLower }}
                        </p>
                    </div>
                </div>
            </div>

            <div v-if="loading || errorMessage || !hasChartData" class="chart-panel status-panel">
                <div v-if="loading" class="loading">{{ $t('siteStatsGrowth.loading') }}</div>
                <p v-else-if="errorMessage" class="error">{{ errorMessage }}</p>
                <p v-else class="empty">{{ $t('siteStatsGrowth.noData') }}</p>
            </div>

            <div v-else class="chart-stack">
                <div class="chart-panel">
                    <div class="panel-header">
                        <h2>{{ $t('siteStatsGrowth.growthPerPeriod') }}</h2>
                        <p>{{ deltaModeDescription }}</p>
                    </div>
                    <div class="chart-wrapper">
                        <BarChart :chartData="deltaChartData" :options="deltaChartOptions" />
                    </div>
                </div>
            </div>

        </div>

        <div v-if="!embedded" class="ImageDivider_container">
            <div class="ImageDivider_wrapper wrapper">
                <div class="TitleBtnGroup">
                    <h2>{{ $t('siteStatsGrowth.questionsOrRemarks') }}</h2>
                    <BtnPrimary class="BtnPrimary" :iconFillColor="'var(--brand-text-light)'"
                        :label="$t('siteStatsGrowth.sendMessage')" :href="localizedPath('/contact')" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { BarChart } from 'vue-chart-3';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarElement,
    BarController,
    Tooltip,
    Legend
} from 'chart.js';
import { applyGenericSeo } from '@/utils/seoUtils';
import BtnPrimary from '@/atoms/BtnPrimary.vue';
// import BtnSecondary from '@/atoms/BtnSecondary.vue';
import { fetchCurrentSiteStats } from '@/api/apiService';
import { fetchRuntimeJson } from '@/utils/runtimeJson';

// Custom plugin to draw vertical line on hover
const crosshairPlugin = {
    id: 'crosshair',
    afterDraw: (chart) => {
        if (!chart?.tooltip?._active?.length) return;

        const yScale = chart.scales?.y;
        const activePoint = chart.tooltip._active[0];
        const x = activePoint?.element?.x;

        // Only draw on Cartesian charts that expose a Y scale.
        if (!yScale || typeof x !== 'number') return;

        const ctx = chart.ctx;
        const topY = yScale.top;
        const bottomY = yScale.bottom;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.stroke();
        ctx.restore();
    }
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarElement,
    BarController,
    Tooltip,
    Legend,
    crosshairPlugin
);

export default {
    name: 'SiteStatsGrowth',
    props: {
        embedded: {
            type: Boolean,
            default: false,
        },
        active: {
            type: Boolean,
            default: true,
        },
    },
    components: {
        BarChart,
        BtnPrimary,
        // BtnSecondary
    },
    data() {
        return {
            statsRows: [],
            growthData: null,
            loading: false,
            errorMessage: '',
            deltaMode: 'month',
            chartRefreshTimers: [],
        };
    },
    computed: {
        seriesConfig() {
            return [
                {
                    key: 'totalPeople',
                    label: this.$t('siteStatsGrowth.series.people'),
                    color: '#0072b2',
                    fillColor: 'rgba(0, 114, 178, 0.2)'
                },
                {
                    key: 'totalDocuments',
                    label: this.$t('siteStatsGrowth.series.documents'),
                    color: '#e69f00',
                    fillColor: 'rgba(230, 159, 0, 0.2)'
                },
                {
                    key: 'totalPhotos',
                    label: this.$t('siteStatsGrowth.series.photos'),
                    color: '#009e73',
                    fillColor: 'rgba(0, 158, 115, 0.2)'
                },
                {
                    key: 'totalPlaces',
                    label: this.$t('siteStatsGrowth.series.places'),
                    color: '#cc79a7',
                    fillColor: 'rgba(204, 121, 167, 0.2)'
                },
                {
                    key: 'uniqueLastnames',
                    label: this.$t('siteStatsGrowth.series.lastnames'),
                    color: '#d55e00',
                    fillColor: 'rgba(213, 94, 0, 0.2)'
                }
            ];
        },
        hasData() {
            return this.statsRows.length > 0;
        },
        hasChartData() {
            return this.chartRows.length > 0;
        },
        latest() {
            if (!this.statsRows.length) return null;
            return this.statsRows[this.statsRows.length - 1];
        },
        summaryItems() {
            if (!this.latest) return [];
            const baseRow = this.statsRows[0];
            const bucketInfo = this.getLatestBucketInfo();

            return this.seriesConfig.map((series) => {
                const deltaSinceStart = this.latest[series.key] - baseRow[series.key];
                const deltaPeriod = bucketInfo.previous
                    ? bucketInfo.current[series.key] - bucketInfo.previous[series.key]
                    : null;

                return {
                    label: series.label,
                    value: this.latest[series.key],
                    deltaSinceStart,
                    deltaPeriod
                };
            });
        },
        trackingStartDate() {
            if (!this.statsRows.length) return null;
            if (this.growthData && this.growthData.trackingStart) return this.growthData.trackingStart;
            return this.statsRows[0].date;
        },
        trackingStartLabel() {
            if (!this.trackingStartDate) return '';
            return this.formatDate(this.trackingStartDate);
        },
        deltaModeLabel() {
            switch (this.deltaMode) {
                case 'week':
                    return this.$t('siteStatsGrowth.period.week');
                case 'month':
                    return this.$t('siteStatsGrowth.period.month');
                case 'year':
                    return this.$t('siteStatsGrowth.period.year');
                default:
                    return this.$t('siteStatsGrowth.period.day');
            }
        },
        deltaModeLabelLower() {
            switch (this.deltaMode) {
                case 'week':
                    return this.$t('siteStatsGrowth.periodLower.week');
                case 'month':
                    return this.$t('siteStatsGrowth.periodLower.month');
                case 'year':
                    return this.$t('siteStatsGrowth.periodLower.year');
                default:
                    return this.$t('siteStatsGrowth.periodLower.day');
            }
        },
        deltaModeDescription() {
            switch (this.deltaMode) {
                case 'week':
                    return this.$t('siteStatsGrowth.deltaDescription.week');
                case 'month':
                    return this.$t('siteStatsGrowth.deltaDescription.month');
                case 'year':
                    return this.$t('siteStatsGrowth.deltaDescription.year');
                default:
                    return this.$t('siteStatsGrowth.deltaDescription.day');
            }
        },
        chartLabels() {
            if (!this.chartRows.length) {
                return [];
            }
            return this.chartRows.map((row) => row.bucketKey);
        },
        chartRows() {
            return this.buildBucketRows('month');
        },
        displayStatsRows() {
            return this.statsRows.filter((row) => this.getBucketKey(row.date, 'month') !== '2026-01');
        },
        absoluteChartData() {
            if (!this.chartRows.length) {
                return { labels: [], datasets: [] };
            }
            return {
                labels: this.chartLabels,
                datasets: this.seriesConfig.map((series) => ({
                    label: series.label,
                    data: this.chartRows.map((row) => row[series.key]),
                    borderColor: series.color,
                    backgroundColor: series.fillColor,
                    tension: 0.25,
                    pointRadius: 2,
                    borderWidth: 2
                }))
            };
        },
        indexChartData() {
            if (!this.chartRows.length) {
                return { labels: [], datasets: [] };
            }

            return {
                labels: this.chartLabels,
                datasets: this.seriesConfig.map((series) => {
                    const baseValue = this.chartRows[0]?.[series.key];
                    const data = this.chartRows.map((row) => {
                        if (!baseValue) return null;
                        return (row[series.key] / baseValue) * 100;
                    });

                    return {
                        label: series.label,
                        data,
                        borderColor: series.color,
                        backgroundColor: series.fillColor,
                        tension: 0.25,
                        pointRadius: 2,
                        borderWidth: 2
                    };
                })
            };
        },
        deltaChartData() {
            if (!this.chartRows.length) {
                return { labels: [], datasets: [] };
            }

            return this.buildBucketDeltaData('month');
        },
        absoluteChartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false
                },
                plugins: {
                    legend: {
                        position: 'top',
                        labels: {
                            boxWidth: 12,
                            boxHeight: 12
                        }
                    },
                    tooltip: {
                        callbacks: {
                            title: (items) => {
                                if (!items.length) return '';
                                return this.formatBucketLabel(items[0].label, 'month');
                            },
                            label: (context) => {
                                const value = context.parsed.y;
                                return `${context.dataset.label}: ${this.formatNumber(value)}`;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        ticks: {
                            maxRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 8,
                            callback: (value) => {
                                const label = this.absoluteChartData.labels[value];
                                return this.formatBucketLabel(label, 'month', true);
                            }
                        }
                    },
                    y: {
                        ticks: {
                            callback: (value) => this.formatNumber(value)
                        }
                    }
                }
            };
        },
        indexChartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false
                },
                plugins: {
                    legend: {
                        position: 'top',
                        labels: {
                            boxWidth: 12,
                            boxHeight: 12
                        }
                    },
                    tooltip: {
                        callbacks: {
                            title: (items) => {
                                if (!items.length) return '';
                                return this.formatBucketLabel(items[0].label, 'month');
                            },
                            label: (context) => {
                                const value = context.parsed.y;
                                return `${context.dataset.label}: ${this.formatNumber(value)}%`;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        ticks: {
                            maxRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 8,
                            callback: (value) => {
                                const label = this.indexChartData.labels[value];
                                return this.formatBucketLabel(label, 'month', true);
                            }
                        }
                    },
                    y: {
                        ticks: {
                            callback: (value) => `${this.formatNumber(value)}%`
                        }
                    }
                }
            };
        },
        deltaChartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false
                },
                plugins: {
                    legend: {
                        position: 'top',
                        labels: {
                            boxWidth: 12,
                            boxHeight: 12
                        }
                    },
                    tooltip: {
                        callbacks: {
                            title: (items) => {
                                if (!items.length) return '';
                                return this.formatBucketLabel(items[0].label, 'month');
                            },
                            label: (context) => {
                                const value = context.parsed.y;
                                return `${context.dataset.label}: ${this.formatDelta(value)}`;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        stacked: true,
                        ticks: {
                            maxRotation: 0,
                            autoSkip: true,
                            maxTicksLimit: 8,
                            callback: (value) => {
                                const label = this.deltaChartData.labels[value];
                                return this.formatBucketLabel(label, 'month', true);
                            }
                        }
                    },
                    y: {
                        stacked: true,
                        ticks: {
                            callback: (value) => this.formatDelta(value)
                        }
                    }
                }
            };
        }
    },
    async mounted() {
        if (!this.embedded) applyGenericSeo({
            title: this.$t('siteStatsGrowth.seoTitle'),
            description: this.$t('siteStatsGrowth.seoDescription')
        });
        window.addEventListener('focus', this.handleVisibilityOrFocusRefresh);
        document.addEventListener('visibilitychange', this.handleVisibilityOrFocusRefresh);
        this.hydrateGrowthData();
    },
    beforeUnmount() {
        window.removeEventListener('focus', this.handleVisibilityOrFocusRefresh);
        document.removeEventListener('visibilitychange', this.handleVisibilityOrFocusRefresh);
        this.clearChartRefreshTimers();
    },
    watch: {
        active(isActive) {
            if (!isActive) return;

            this.scheduleChartRefresh();

            // Retry hydration when this tab becomes active but still has no rows.
            if (!this.loading && !this.errorMessage && !this.hasData) {
                this.hydrateGrowthData();
            }
        }
    },
    methods: {
        clearChartRefreshTimers() {
            this.chartRefreshTimers.forEach((id) => clearTimeout(id));
            this.chartRefreshTimers = [];
        },
        scheduleChartRefresh() {
            this.clearChartRefreshTimers();

            // Run multiple resize passes for throttled/background-tab scenarios.
            this.refreshCharts();
            this.chartRefreshTimers.push(setTimeout(() => this.refreshCharts(), 120));
            this.chartRefreshTimers.push(setTimeout(() => this.refreshCharts(), 360));
        },
        handleVisibilityOrFocusRefresh() {
            if (document.hidden) return;
            if (!this.active) return;
            this.scheduleChartRefresh();
        },
        refreshCharts() {
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    Object.values(ChartJS.instances || {}).forEach((chart) => {
                        try {
                            chart?.resize();
                            chart?.update('none');
                        } catch (error) {
                            // Ignore stale/destroyed chart references during tab switches.
                        }
                    });
                });
            });
        },
        async hydrateGrowthData() {
            if (this.loading) return;
            this.loading = true;
            this.errorMessage = '';

            this.growthData = await fetchRuntimeJson('/data/getSiteStats/siteStatsGrowth.json', null);

            if (!this.growthData || !Array.isArray(this.growthData.rows)) {
                this.errorMessage = this.$t('siteStatsGrowth.noData');
                this.loading = false;
                return;
            }

            // Shift JSON dates back by 1 day: the cronjob runs at 2 AM, so each entry is
            // dated with the run date but represents the state at the end of the previous day.
            let rows = this.growthData.rows.map(row => {
                const d = new Date(`${row.date}T12:00:00`);
                d.setDate(d.getDate() - 1);
                return { ...row, date: d.toISOString().split('T')[0] };
            });

            // Fetch current stats and update/append today's data with live counts
            try {
                const currentStats = await fetchCurrentSiteStats();
                const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

                // Find if today's data already exists
                const todayIndex = rows.findIndex(row => row.date === today);

                if (todayIndex !== -1) {
                    // Update existing entry with live data
                    rows[todayIndex] = {
                        date: today,
                        ...currentStats
                    };
                } else {
                    // Append today's current stats
                    rows.push({
                        date: today,
                        ...currentStats
                    });
                }
            } catch (error) {
                console.error('Failed to fetch current stats, using historical data only:', error);
                // Continue with just the historical data
            }

            // Filter out days with no changes
            rows = this.filterUnchangedDays(rows);

            this.statsRows = rows;
            this.loading = false;

            // Option 3: wait for Vue to finish rendering the charts before resizing.
            // Without this, Chart.js measures a 0×0 canvas because the chart panels
            // are still hidden (v-if on !loading) when loading flips to false.
            this.scheduleChartRefresh();

            // Option 1: notify the parent that charts are ready so it can also
            // call refreshAllCharts() at the correct moment.
            this.$emit('ready');
        },
        filterUnchangedDays(rows) {
            if (rows.length <= 1) return rows;

            const filtered = [rows[0]]; // Always keep the first day

            for (let i = 1; i < rows.length; i++) {
                const current = rows[i];
                const previous = rows[i - 1];

                // Check if any value changed
                const hasChanges =
                    current.totalPeople !== previous.totalPeople ||
                    current.totalDocuments !== previous.totalDocuments ||
                    current.totalPhotos !== previous.totalPhotos ||
                    current.totalPlaces !== previous.totalPlaces ||
                    current.uniqueLastnames !== previous.uniqueLastnames;

                // Keep this day if there are changes, or if it's the last day
                if (hasChanges || i === rows.length - 1) {
                    filtered.push(current);
                }
            }

            return filtered;
        },
        formatNumber(value) {
            if (value === null || value === undefined) return '-';
            return Number(value).toLocaleString(this.localeTag());
        },
        getLatestBucketInfo() {
            const buckets = this.buildBucketRows('month');
            if (!buckets.length) return { current: this.latest, previous: null };

            const current = buckets[buckets.length - 1];
            const previous = buckets.length > 1 ? buckets[buckets.length - 2] : null;
            return { current, previous };
        },
        buildBucketDeltaData(mode = 'month') {
            const bucketRows = this.buildBucketRows(mode);

            return {
                labels: bucketRows.map((row) => row.bucketKey),
                datasets: this.seriesConfig.map((series) => ({
                    label: series.label,
                    data: bucketRows.map((row, index) => {
                        const previous = index === 0
                            ? row._bucketStart?.[series.key]
                            : bucketRows[index - 1]?.[series.key];
                        return row[series.key] - previous;
                    }),
                    borderColor: series.color,
                    backgroundColor: series.fillColor,
                    borderWidth: 1
                }))
            };
        },
        buildBucketRows(mode) {
            const rows = this.displayStatsRows;

            if (mode === 'day') {
                return rows.map((row) => ({ ...row, bucketKey: row.date }));
            }

            const bucketKeys = [];
            const byKey = new Map();

            rows.forEach((row) => {
                const bucketKey = this.getBucketKey(row.date, mode);
                if (!byKey.has(bucketKey)) {
                    byKey.set(bucketKey, { bucketKey, ...row, _bucketStart: row });
                    bucketKeys.push(bucketKey);
                } else {
                    const existing = byKey.get(bucketKey);
                    byKey.set(bucketKey, { ...existing, ...row });
                }
            });

            return bucketKeys.map((bucketKey) => byKey.get(bucketKey));
        },
        getBucketKey(dateValue, mode) {
            switch (mode) {
                case 'week':
                    return this.getIsoWeekKey(dateValue);
                case 'month':
                    return String(dateValue).slice(0, 7);
                case 'year':
                    return String(dateValue).slice(0, 4);
                default:
                    return dateValue;
            }
        },
        getIsoWeekKey(dateValue) {
            const date = new Date(`${dateValue}T00:00:00`);
            if (Number.isNaN(date.getTime())) return dateValue;

            const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            const dayNumber = target.getUTCDay() || 7;
            target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
            const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
            const weekNumber = Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
            const paddedWeek = String(weekNumber).padStart(2, '0');
            return `${target.getUTCFullYear()}-W${paddedWeek}`;
        },
        formatBucketLabel(bucketKey, mode, short = false) {
            if (!bucketKey) return '';

            if (mode === 'day') {
                return this.formatDate(bucketKey);
            }

            if (mode === 'week') {
                const [year, week] = String(bucketKey).split('-W');
                if (!week) return String(bucketKey);
                return short ? `${year}-W${week}` : `${this.$t('siteStatsGrowth.period.week')} ${week} ${year}`;
            }

            if (mode === 'month') {
                const [year, month] = String(bucketKey).split('-');
                if (!month) return String(bucketKey);
                const parsed = new Date(Number(year), Number(month) - 1, 1);
                if (Number.isNaN(parsed.getTime())) return String(bucketKey);
                return parsed.toLocaleDateString(this.localeTag(), {
                    month: short ? 'short' : 'long',
                    year: 'numeric'
                });
            }

            return String(bucketKey);
        },
        formatDelta(value) {
            if (value === null || value === undefined) return '-';
            const signed = Number(value);
            if (!Number.isFinite(signed)) return '-';
            const prefix = signed > 0 ? '+' : '';
            return `${prefix}${this.formatNumber(signed)}`;
        },
        formatDate(value) {
            if (!value) return '-';
            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) return value;
            return parsed.toLocaleDateString(this.localeTag(), {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        },
        localeTag() {
            const locale = this.$i18n?.locale?.value ?? this.$i18n?.locale;
            if (locale === 'en') return 'en-US';
            if (locale === 'id') return 'id-ID';
            return 'nl-NL';
        },
        localizedPath(path) {
            const locale = this.$route?.params?.locale;
            return locale ? `/${locale}${path}` : path;
        }
    }
};
</script>

<style scoped>
.stats-header {
    position: relative;
    gap: 8px;
}

.stats-header-actions {
    position: absolute;
    top: 0;
    left: 20px;
}

.updated-at {
    color: var(--brand-text-muted, #6f6a63);
}

.stats-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 16px;
}

.summary-stack {
    /* display: grid; */
    display: none;
    /* Temporarily disabled */
    gap: 16px;
    margin-bottom: 24px;
}

.summary-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
}

.summary-title h2 {
    margin: 0 0 4px;
}

.summary-title p {
    margin: 0;
    color: var(--brand-text-muted, #6f6a63);
}

.summary-card {
    background: rgba(255, 255, 255, 0.9);
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.06);
}

.summary-label {
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--brand-text-muted, #6f6a63);
}

.summary-value {
    font-size: 1.4rem;
    font-weight: 600;
    margin-top: 6px;
    color: var(--brand-text-dark, #2b2621);
}

.summary-delta {
    display: inline-block;
    margin-left: 8px;
    font-size: 0.85rem;
    font-weight: 600;
    color: #a23b3b;
}

.summary-period {
    margin: 6px 0 0;
    font-size: 0.85rem;
    color: var(--brand-text-muted, #6f6a63);
}

.chart-panel {
    background: rgba(255, 255, 255, 0.9);
    border-radius: 20px;
    padding: 24px;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.08);
}

.warning-banner {
    margin-top: 24px;
    padding: 24px;
    border-radius: var(--border-radius-md);
    background: rgba(174, 30, 37, 0.05);
    border: 2px dashed rgb(174 30 37);
    font-weight: 600;
    line-height: 110%;
    color: var(--brand-color-1);
    text-align: center;
}

.warning-banner p {
    margin: 0;
}

.warning-banner p+p {
    margin-top: 6px;
}

.ImageDivider_container {
    background: url('@/assets/noise128x128.png'), linear-gradient(to bottom, var(--brand-bg-2) 50%, var(--brand-bg-4) 50%);
    background-repeat: repeat;
    background-size: auto;
    transition: var(--transition-bounce);
}

.ImageDivider_wrapper {
    padding-left: 20px;
    padding-right: 20px;
}

.ImageDivider_wrapper .TitleBtnGroup {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
    background-color: var(--brand-bg-1);
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-card-sm);
    padding: 96px;
}

.chart-stack {
    display: grid;
    gap: 24px;
}

.panel-header h2 {
    margin: 0 0 6px;
}

.panel-header p {
    margin: 0 0 16px;
    color: var(--brand-text-muted, #6f6a63);
}

.chart-wrapper {
    height: 420px;
}

.loading,
.error,
.empty {
    text-align: center;
    padding: 32px 0;
    color: var(--brand-text-muted, #6f6a63);
}

.error {
    color: #9c2b2b;
}

.status-panel {
    margin-bottom: 24px;
}


@media (max-width: 900px) {
    .header {
        padding-top: 24px;
    }

    .chart-wrapper {
        height: 320px;
    }
}
</style>
